import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { usePageTitle } from "@/hooks/use-page-title";
import { useGuildRoute } from "@/hooks/use-guild-route";
import { useGuildSubscriptionFeatures } from "@/hooks/use-guild-subscription-features";
import { useGuildPermissions } from "@/hooks/use-guild-permissions";
import { UpgradeBanner } from "@/components/subscription/upgrade-banner";
import { FeatureLock } from "@/components/subscription/feature-lock";
import { ThreadQAList, ThreadQAConfigEditor } from "@/components/thread-qa";

type ThreadsQASearch = {
  configId?: number;
  new?: boolean;
};

export const Route = createFileRoute(
  "/dashboard/guilds/$applicationGuildId/threads-qa"
)({
  component: ThreadsQAPage,
  validateSearch: (search: Record<string, unknown>): ThreadsQASearch => ({
    configId: search.configId ? Number(search.configId) : undefined,
    new: search.new === true ? true : undefined,
  }),
});

function ThreadsQAPage() {
  usePageTitle("Threads Q&A");
  const { applicationGuildId, isLocked } = useGuildRoute();
  const { isFreePlan, features } = useGuildSubscriptionFeatures();
  const { canEdit } = useGuildPermissions();
  const isReadOnly = isLocked || !canEdit("threadsQA");
  const { configId, new: isCreating } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const showEditor = isCreating || configId !== undefined;

  const handleBack = () => {
    navigate({ search: {} });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-balance text-3xl font-bold">Threads Q&A</h1>
        <p className="text-muted-foreground text-pretty">
          Configure scoped AI-powered Q&A for channel threads
        </p>
      </div>

      {isFreePlan && features && !features.threadsQA && (
        <UpgradeBanner
          feature="Threads Q&A"
          description="Upgrade to Pro to enable scoped AI Q&A in channel threads with document-level control."
        />
      )}

      <FeatureLock
        locked={isFreePlan && !!features && !features.threadsQA}
        label="Pro feature"
      >
        {showEditor ? (
          <ThreadQAConfigEditor
            applicationGuildId={applicationGuildId}
            configId={configId}
            onBack={handleBack}
            onCreated={(newConfigId) => {
              navigate({ search: { configId: newConfigId } });
            }}
          />
        ) : (
          <ThreadQAList
            applicationGuildId={applicationGuildId}
            isLocked={isReadOnly}
            onEdit={(id) => navigate({ search: { configId: id } })}
            onCreateNew={() => navigate({ search: { new: true } })}
          />
        )}
      </FeatureLock>
    </div>
  );
}
